const usageState = {
  selectedByField: {},
  currentByField: {},
  // Both versions of a field, for the ones an agent search covered.
  sourcesByField: {},
  // What the research agent said about the company, for the same run.
  webResearch: null,
};

export const recordSuggestionSelection = (
  fieldKey,
  selectedValue,
  fileId,
  runId
) => {
  usageState.selectedByField[fieldKey] = {
    selected: selectedValue ?? "",
    fileId,
    runId,
  };
};

export const recordFieldValue = (fieldKey, value) => {
  usageState.currentByField[fieldKey] = value ?? "";
};

/**
 * Record both versions of a field, whether or not they agreed.
 *
 * The submission has one slot per field, so carrying a value forward discards the other
 * one. Keeping the pair here means the choice stays answerable afterwards: what the
 * documents said, what the web said, which was used, and whether it was flagged.
 */
export const recordFieldSources = (fieldKey, sources) => {
  usageState.sourcesByField[fieldKey] = {
    submission: sources?.submission ?? null,
    web: sources?.web ?? null,
    preferred: sources?.preferred ?? null,
    flagged: Boolean(sources?.flagged),
    verdict: sources?.verdict ?? null,
    source_urls: sources?.source_urls ?? [],
    reasoning: sources?.reasoning ?? null,
    confidence: sources?.confidence ?? null,
  };
};

/** The company summary from the research run these field sources came from. */
export const recordWebResearch = (research) => {
  usageState.webResearch = research?.company_summary
    ? { company_summary: research.company_summary }
    : usageState.webResearch;
};

const emptyUsage = (runId) => ({
  selected_fields: {},
  changed_fields: {},
  unchanged_fields: {},
  field_sources: {},
  run_id: runId,
});

/**
 * @param fallbackFileId where to attach source records for fields no file produced. A
 *   web-sourced field has no document selection behind it, so without this its record
 *   would be dropped on the way out.
 */
export const buildFieldUsageByFile = (fallbackFileId = null) => {
  const usageByFile = {};
  Object.entries(usageState.selectedByField).forEach(
    ([fieldKey, selection]) => {
      const fileId = selection?.fileId;
      if (!fileId) {
        return;
      }

      if (!usageByFile[fileId]) {
        usageByFile[fileId] = emptyUsage(selection?.runId);
      }

      const selectedValue = selection?.selected ?? "";
      const finalValue = usageState.currentByField[fieldKey] ?? "";
      const usage = usageByFile[fileId];

      usage.selected_fields[fieldKey] = selectedValue;

      // Only present for fields an agent search covered; a single-source field has
      // nothing to record.
      const sources = usageState.sourcesByField[fieldKey];
      if (sources) {
        usage.field_sources[fieldKey] = sources;
      }

      if (finalValue === selectedValue) {
        usage.unchanged_fields[fieldKey] = finalValue;
      } else {
        usage.changed_fields[fieldKey] = {
          selected: selectedValue,
          final: finalValue,
        };
      }
    }
  );

  // Fields the web answered but no document did have no selection to hang off, so they
  // are attached here rather than lost.
  if (fallbackFileId) {
    Object.entries(usageState.sourcesByField).forEach(([fieldKey, sources]) => {
      const alreadyRecorded = Object.values(usageByFile).some(
        (usage) => usage.field_sources[fieldKey]
      );
      if (alreadyRecorded) {
        return;
      }
      if (!usageByFile[fallbackFileId]) {
        usageByFile[fallbackFileId] = emptyUsage(undefined);
      }
      usageByFile[fallbackFileId].field_sources[fieldKey] = sources;
    });
  }

  // The summary describes the run, so it belongs on whichever buckets carry its fields.
  if (usageState.webResearch?.company_summary) {
    Object.values(usageByFile).forEach((usage) => {
      if (Object.keys(usage.field_sources).length > 0) {
        usage.web_company_summary = usageState.webResearch.company_summary;
      }
    });
  }

  return usageByFile;
};

export const getUsedFieldKeys = () => {
  return new Set(
    Object.keys(usageState.selectedByField).filter((fieldKey) => {
      const current = usageState.currentByField[fieldKey];
      return current != null && current !== "";
    })
  );
};

export const clearFieldUsage = () => {
  usageState.selectedByField = {};
  usageState.currentByField = {};
  usageState.sourcesByField = {};
  usageState.webResearch = null;
};
